import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, Dialog, Field } from "@secretaa/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../app/api.ts";
import { canManage, useCurrentWorkspace, useSession } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { Form, FormError, Notice, PageHeader } from "../../shared/form.tsx";

type Role = "owner" | "admin" | "member";

export function MembersPage() {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const me = useSession().data?.user.id;
  const qc = useQueryClient();
  const manage = canManage(cur.role);
  const members = useQuery({ queryKey: qk.members(cur.tenant), queryFn: () => api.members.list(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const units = useQuery({ queryKey: qk.orgUnits(cur.tenant), queryFn: () => api.orgUnits.list(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const invitations = useQuery({ queryKey: qk.invitations(cur.tenant), queryFn: () => api.invitations.list(cur.workspaceId), enabled: manage, staleTime: STALE_TIME.auditFeed });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [scopeFor, setScopeFor] = useState<{ userId: string; version: number; unitIds: string[] } | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: qk.members(cur.tenant) });
    await qc.invalidateQueries({ queryKey: qk.me() });
  };
  const invite = useMutation({
    mutationFn: () => api.invitations.create(cur.workspaceId, { email, role: inviteRole }),
    onSuccess: async () => {
      setInviteOpen(false);
      await qc.invalidateQueries({ queryKey: qk.invitations(cur.tenant) });
    },
  });
  const setRole = useMutation({ mutationFn: (v: { userId: string; role: Role; version: number }) => api.members.setRole(cur.workspaceId, v.userId, v.role, v.version), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (userId: string) => api.members.remove(cur.workspaceId, userId), onSuccess: refresh });
  const setScope = useMutation({
    mutationFn: (v: { userId: string; version: number; orgScope: "all" | "restricted"; unitIds: string[] }) =>
      api.members.setScope(cur.workspaceId, v.userId, { orgScope: v.orgScope, unitIds: v.unitIds }, v.version),
    onSuccess: async () => {
      setScopeFor(null);
      await refresh();
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.invitations.revoke(cur.workspaceId, id), onSuccess: () => qc.invalidateQueries({ queryKey: qk.invitations(cur.tenant) }) });
  const err = [invite, setRole, remove, setScope, revoke].find((m) => m.isError)?.error;
  const activeUnits = units.data?.filter((u) => !u.archived) ?? [];
  return (
    <>
      <PageHeader title={t("members.title")} actions={manage ? <Button onClick={() => setInviteOpen(true)}>{t("members.invite")}</Button> : null} />
      <FormError message={err ? errorMessage(t, err) : members.isError ? errorMessage(t, members.error) : null} />
      {invite.isSuccess ? <Notice>{t("members.invited", { email })}</Notice> : null}
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-(--saa-border) text-left text-(--saa-fg-muted)">
            <tr>
              <th className="px-4 py-2 font-medium">{t("auth.fields.displayName")}</th>
              <th className="px-4 py-2 font-medium">{t("members.role")}</th>
              <th className="px-4 py-2 font-medium">{t("members.scope")}</th>
              {manage ? <th className="px-4 py-2" /> : null}
            </tr>
          </thead>
          <tbody>
            {members.data?.map((m) => (
              <tr key={m.userId} className="border-b border-(--saa-border) last:border-0">
                <td className="px-4 py-2">
                  <p className="font-medium">
                    {m.displayName} {m.userId === me ? <span className="text-(--saa-fg-muted)">({t("members.you")})</span> : null}
                  </p>
                  <p className="text-xs text-(--saa-fg-muted)">{m.email}</p>
                </td>
                <td className="px-4 py-2">
                  {manage && m.userId !== me ? (
                    <select
                      aria-label={t("members.role")}
                      className="rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 py-1"
                      value={m.role}
                      onChange={(e) => setRole.mutate({ userId: m.userId, role: e.target.value as Role, version: m.version })}
                    >
                      {(["owner", "admin", "member"] as const).map((r) => (
                        <option key={r} value={r}>
                          {t(`members.roles.${r}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    t(`members.roles.${m.role}`)
                  )}
                </td>
                <td className="px-4 py-2">
                  {m.orgScope === "all" ? t("members.scopes.all") : m.unitIds.map((id) => units.data?.find((u) => u.id === id)?.name ?? "—").join(", ")}
                  {manage && activeUnits.length > 0 ? (
                    <Button variant="ghost" className="ml-2 h-7 px-2" onClick={() => setScopeFor({ userId: m.userId, version: m.version, unitIds: m.unitIds })}>
                      {t("common.edit")}
                    </Button>
                  ) : null}
                </td>
                {manage ? (
                  <td className="px-4 py-2 text-right">
                    {m.userId !== me ? (
                      <Button variant="ghost" onClick={() => remove.mutate(m.userId)}>
                        {t("members.remove")}
                      </Button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {manage && invitations.data?.length ? (
        <section className="mt-6">
          <h2 className="mb-2 font-semibold">{t("members.pendingInvitations")}</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {invitations.data.map((i) => (
              <li key={i.id} className="flex items-center justify-between rounded-md border border-(--saa-border) px-3 py-2">
                <span>
                  {i.email} · {t(`members.roles.${i.role}`)} · {t(`members.invitationStatus.${i.status}`)}
                </span>
                {i.status === "pending" ? (
                  <Button variant="ghost" onClick={() => revoke.mutate(i.id)}>
                    {t("members.revoke")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen} title={t("members.invite")} closeLabel={t("common.close")}>
        <Form label={t("members.invite")} onSubmit={() => invite.mutate()}>
          <Field label={t("auth.fields.email")} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("members.role")}
            <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as "member" | "admin")}>
              <option value="member">{t("members.roles.member")}</option>
              <option value="admin">{t("members.roles.admin")}</option>
            </select>
          </label>
          <Button type="submit" disabled={invite.isPending || !email}>
            {t("members.inviteSubmit")}
          </Button>
        </Form>
      </Dialog>
      <Dialog open={scopeFor !== null} onOpenChange={(o) => !o && setScopeFor(null)} title={t("members.scope")} closeLabel={t("common.close")}>
        {scopeFor ? (
          <ScopeEditor
            units={activeUnits}
            initial={scopeFor.unitIds}
            onSave={(orgScope, unitIds) => setScope.mutate({ userId: scopeFor.userId, version: scopeFor.version, orgScope, unitIds })}
          />
        ) : null}
      </Dialog>
    </>
  );
}

function ScopeEditor({ units, initial, onSave }: { units: { id: string; name: string }[]; initial: string[]; onSave: (s: "all" | "restricted", ids: string[]) => void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"all" | "restricted">(initial.length ? "restricted" : "all");
  const [ids, setIds] = useState<string[]>(initial);
  return (
    <Form label={t("members.scope")} onSubmit={() => onSave(mode, mode === "all" ? [] : ids)}>
      <fieldset className="flex flex-col gap-2 text-sm">
        <legend className="sr-only">{t("members.scope")}</legend>
        {(["all", "restricted"] as const).map((m) => (
          <label key={m} className="flex items-center gap-2">
            <input type="radio" name="scope" checked={mode === m} onChange={() => setMode(m)} />
            {t(`members.scopes.${m}`)}
          </label>
        ))}
      </fieldset>
      {mode === "restricted" ? (
        <fieldset className="flex max-h-48 flex-col gap-1 overflow-auto text-sm">
          <legend className="mb-1 font-medium">{t("members.chooseUnits")}</legend>
          {units.map((u) => (
            <label key={u.id} className="flex items-center gap-2">
              <input type="checkbox" checked={ids.includes(u.id)} onChange={(e) => setIds(e.target.checked ? [...ids, u.id] : ids.filter((x) => x !== u.id))} />
              {u.name}
            </label>
          ))}
        </fieldset>
      ) : null}
      <Button type="submit" disabled={mode === "restricted" && ids.length === 0}>
        {t("common.save")}
      </Button>
    </Form>
  );
}
